import { AxiosError } from "axios";
import { ADMIN_API_ENDPOINTS } from "@/config/apiEndpoints/adminUrls";
import { handleApiError } from "@/lib/utils";
import { UNIVERSITY_ID } from "@/constants/admin";
import { upsertUniversitySettings, getUniversitySettingsBySection, getAllUniversitySettings } from "./universitySettings";
import { UNIVERSITY_SETTINGS_SECTION_TYPES } from "@/constants/admin";
import adminAxios from "@/lib/axios";

export interface ILeadDropForm {
  id?: number;
  university_settings_id?: number;
  title?: string;
  sub_text?: string;
  checkbox_description?: string;
  /**
   * Optional consent links referenced by {{N:Label}} tokens in `checkbox_description`.
   * Slot 1 = Terms & Conditions, slot 2 = Privacy Policy. Empty keeps current behaviour.
   */
  terms_conditions_link?: string;
  privacy_policy_link?: string;
  button_text?: string;
  button_color?: string;
  button_icon?: string;
  button_icon_alt?: string;
  button_text_color?: string;
  button_background_color?: string;
  /** Variant 10 configurable UI colors */
  title_color?: string;
  sub_text_color?: string;
  button_disabled_color?: string;
  checkbox_color?: string;
  success_icon?: string;
  success_icon_alt?: string;
  success_title?: string;
  success_description?: string;
  success_description_text_color?: string;
  success_description_color?: string;
  success_button_icon?: string;
  success_button_icon_alt?: string;
  success_button_text?: string;
  success_button_text_color?: string;
  success_button_background_color?: string;
  /** Optional URL for the success/thank-you button. When set, opens in a new tab; empty keeps current behaviour. */
  success_button_link?: string;
  /** Variant 6 segmented intake (also stored in additional_fields) */
  seg3_success_highlight_text?: string;
  seg3_success_highlight_color?: string;
  seg3_success_disclaimer?: string;
  seg3_success_disclaimer_color?: string;
  seg3_success_button_url?: string;
  seg4_subtitle?: string;
  seg4_subtitle_color?: string;
  seg4_button_url?: string;
  seg4_disclaimer?: string;
  seg4_disclaimer_color?: string;
  seg4_highlight_text?: string;
  seg4_highlight_color?: string;
  /** Form 2 and other JSON-stored fields (merged at top level when loading) */
  additional_fields?: Record<string, unknown>;
}

/**
 * Set lead drop form variant for university (upsert)
 */
export async function setLeadDropFormVariant(variant: string, isActive: boolean = true) {
  try {
    const response = await upsertUniversitySettings(
      UNIVERSITY_SETTINGS_SECTION_TYPES.LEAD_DROP_FORM,
      variant,
      isActive
    );
    return response;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

/**
 * Create lead drop form entry
 * Note: payload should include university_settings_id (not university_id and variant)
 */
export async function createLeadDropForm(payload: Partial<ILeadDropForm>) {
  try {
    const response = await adminAxios.post(ADMIN_API_ENDPOINTS.LEAD_DROP_FORM.CREATE,
      payload
    );
    if (response?.status !== 201) return null;
    return unwrapLeadDropAdminBody<ILeadDropForm>(response.data);
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

/**
 * Update lead drop form metadata
 */
export async function updateLeadDropForm(id: number, payload: Partial<ILeadDropForm>) {
  try {
    const response = await adminAxios.put(ADMIN_API_ENDPOINTS.LEAD_DROP_FORM.UPDATE(id),
      payload
    );
    if (response?.status !== 200) return null;
    return unwrapLeadDropAdminBody<ILeadDropForm>(response.data);
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

/**
 * Admin list/detail responses use `{ success: true, data: T }` (see server APIResponse).
 * Normalize so callers receive `T` directly.
 */
function unwrapLeadDropAdminBody<T>(body: unknown): T | null {
  if (body == null) return null;
  if (typeof body !== "object") return body as T;
  const o = body as { success?: boolean; data?: T };
  if (o.success === true && "data" in o) return (o.data ?? null) as T | null;
  return body as T;
}

/**
 * Get all lead drop forms for university
 */
export async function getAllLeadDropForms() {
  try {
    const response = await adminAxios.get(ADMIN_API_ENDPOINTS.LEAD_DROP_FORM.GET_BY_UNIVERSITY_ID(UNIVERSITY_ID)
    );
    if (response?.status !== 200) return null;
    const unwrapped = unwrapLeadDropAdminBody<ILeadDropForm[]>(response.data);
    return unwrapped ?? [];
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

/**
 * Get lead drop form by variant
 * Returns null silently for 404 (expected when variant doesn't exist yet)
 */
export async function getLeadDropFormByVariant(variant: string) {
  try {
    const response = await adminAxios.get(ADMIN_API_ENDPOINTS.LEAD_DROP_FORM.GET_BY_UNIVERSITY_AND_VARIANT(UNIVERSITY_ID, variant)
    );
    if (response?.status !== 200) return null;
    return unwrapLeadDropAdminBody<ILeadDropForm>(response.data);
  } catch (error) {
    const axiosError = error as AxiosError;
    // 404 is expected when checking if data exists - return null silently
    if (axiosError.response?.status === 404) {
      return null;
    }
    const errorResponse = handleApiError(axiosError);
    return errorResponse ? errorResponse : null;
  }
}

/**
 * Delete lead drop form
 */
export async function deleteLeadDropForm(id: number) {
  try {
    const response = await adminAxios.delete(ADMIN_API_ENDPOINTS.LEAD_DROP_FORM.UPDATE(id)
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

/**
 * Get lead drop form settings by section
 * Returns the active variant and status for lead drop form
 */
export async function getLeadDropForm() {
  try {
    const response = await getUniversitySettingsBySection(UNIVERSITY_SETTINGS_SECTION_TYPES.LEAD_DROP_FORM);
    if (response?.data) {
      // Transform to match expected format
      return {
        data: {
          lead_drop_form_variant: response.data.variant,
          lead_drop_form_status: response.data.is_active,
          university_settings_id: response.data.id,
        }
      };
    } else if (response) {
      // Handle case where response is the data directly (not wrapped)
      return {
        data: {
          lead_drop_form_variant: response.variant,
          lead_drop_form_status: response.is_active,
          university_settings_id: response.id,
        }
      };
    }
    return response;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

